import { queryString } from '@shared/hooks/recoil-query';
import { useCachedResource } from '@shared/hooks/useCachedResource';
import { api_ } from '@shared/plugIn/axios';
import { kstDayEndISO, kstDayStartISO } from '@shared/utils/kstTime.util';

import { OrderStatKind, OrderStatOrder } from './adminOrderStat.types';
import { BrandNameResolver, useAnalyticsBrandNames } from './useAnalyticsBrandNames';

/** 한 번에 끌어올 주문 수 상한 — 넘치면 화면에 "일부만 집계" 경고 */
export const ORDER_FETCH_LIMIT = 20000;

/**
 * 조회 가능한 최대 기간.
 * 반기(약 184일)까지 한 번에 보려고 열어 뒀다 — 실제 안전장치는 이 일수가 아니라
 * ORDER_FETCH_LIMIT(문서 수)이고, 넘치면 화면에 "일부만 집계" 경고가 뜬다.
 */
export const ORDER_MAX_RANGE_DAYS = 200;

//* 상조 표기명은 fevent.bugoBrandAgencyIndex가 있어야 확정된다 — 주문 문서엔 없다
const FEVENT_POPULATE = {
  path: 'fevent',
  select: 'bugoBrandAgencyIndex deceasedInfo.name deceasedInfo.coffinOut.date',
};

/**
 * 상조명은 보통 `useAnalyticsBrandNames`의 이름 맵으로 붙인다 — populate하면
 * `bugoAgencyList`가 문서마다 통째로 복사돼 응답이 부푼다.
 * 이름 맵을 못 받은 경우에만 폴백으로 붙인다.
 */
const BRAND_POPULATE = {
  path: 'bugoBrand',
  select:
    'type bugoAgency.corpName bugoAgency.nickName bugoAgencyList.corpName bugoAgencyList.nickName',
};

const FLOWER_SELECT = [
  'bugoBrand',
  'fevent',
  'type',
  'docStatus',
  'paymentDetail.status',
  'paymentDetail.paymentByCash',
  'paymentDetail.purchasedAt',
  'orderDetail.quantity',
  'orderDetail.shopItemEmbed.name',
  'createdAt',
].join(' ');

const GIFT_SELECT = [
  'bugoBrand',
  'fevent',
  'docStatus',
  'paymentDetail.status',
  'paymentDetail.paymentByCash',
  'paymentDetail.cancelledTotalPrice',
  'paymentDetail.purchasedAt',
  'orderDetails.type',
  'orderDetails.quantity',
  'orderDetails.cancelQuantity',
  'orderDetails.couponDetail.totalCount',
  'orderDetails.giftItemEmbed.name',
  'receiverName',
  'createdAt',
].join(' ');

//* 백엔드 wreathOrderMatch와 같은 조건
const FLOWER_STATUSES = ['Paid', 'Confirmed'];
//* 취소 건도 끌어와야 취소액을 뺄 수 있다 (SUMMARY_PAYMENT_STATUSES)
const GIFT_STATUSES = ['Paid', 'Confirmed', 'CancelRequested', 'Canceled'];

const buildUrl = (
  kind: OrderStatKind,
  from: string,
  to: string,
  brandPopulate: boolean,
): string => {
  const startISO = kstDayStartISO(from);
  const endISO = kstDayEndISO(to);
  const populate = brandPopulate ? [BRAND_POPULATE, FEVENT_POPULATE] : [FEVENT_POPULATE];

  if (kind === 'flower') {
    const query = queryString({
      filter: {
        'paymentDetail.purchasedAt': { $gte: startISO, $lte: endISO },
        'paymentDetail.status': { $in: FLOWER_STATUSES },
        //* 레거시 답례품 주문(ReturnGift)은 화환이 아니다
        type: { $ne: 'ReturnGift' },
        docStatus: { $ne: 'Deleted' },
      },
      populate,
      options: {
        limit: ORDER_FETCH_LIMIT,
        sort: { 'paymentDetail.purchasedAt': -1 },
      },
    });
    //* queryString은 encode:false라 공백이 든 select를 직접 인코딩해야 URL이 안 깨진다
    return `admin/shop-order?${query}&select=${encodeURIComponent(FLOWER_SELECT)}`;
  }

  //* ⚠️ /admin/gift-order는 from/to로 createdAt 범위를 서버가 강제 주입한다.
  //* filter에 createdAt을 넣어도 덮어써지므로 날짜는 반드시 from/to로 넘긴다.
  const query = queryString({
    filter: {
      docStatus: 'Active',
      'paymentDetail.status': { $in: GIFT_STATUSES },
    },
    populate,
    options: { limit: ORDER_FETCH_LIMIT, sort: { createdAt: -1 } },
  });
  return `admin/gift-order?${query}&from=${encodeURIComponent(
    startISO ?? '',
  )}&to=${encodeURIComponent(endISO ?? '')}&select=${encodeURIComponent(GIFT_SELECT)}`;
};

interface OrderStatPayload {
  orders: OrderStatOrder[];
  truncated: boolean;
}

export interface AdminOrderStatState {
  status: 'idle' | 'loading' | 'success' | 'error';
  orders: OrderStatOrder[];
  truncated: boolean;
  errorMessage: string | null;
}

const EMPTY_ORDERS: OrderStatOrder[] = [];

/**
 * 서버 message를 살린 오류 — 화면 문구는 여기서 확정하고 `response`는 그대로 얹어
 * 캐시 훅의 404 판별(`isNotFoundError`)이 계속 동작하게 한다.
 */
const toStatError = (err: unknown): Error => {
  const res = (err as { response?: { status?: number; data?: { message?: string } } })
    ?.response;
  //* 백엔드 전역 필터가 error 코드를 덮어써서 400에서만 서버 message가 의미 있다
  const message =
    res?.status === 400 && res.data?.message
      ? res.data.message
      : res?.status === 401 || res?.status === 403
      ? '조회 권한이 없습니다.'
      : '통계를 불러오지 못했습니다.';
  return Object.assign(new Error(message), { response: res });
};

/**
 * 화환/답례품 주문 원자료 조회.
 * 상조별 집계를 주는 전용 API가 없어(docs/admin-order-stat-spec.md)
 * 제네릭 admin 조회로 받아 프론트에서 접는다.
 *
 * ⚠️ 한 번에 최대 2만 건이라 **재조회를 아끼는 게 이 훅의 절반**이다.
 * 캐시(`useCachedResource`)를 태워서 탭 왕복·기간 되돌리기·재진입에는 요청이 나가지 않고,
 * 같은 키를 연타해도 조회는 한 번만 돈다. (예전엔 useState+useEffect 직접 호출이라
 * 탭을 오갈 때마다 2만 건을 새로 받았다)
 */
export const useAdminOrderStat = (
  kind: OrderStatKind,
  from: string,
  to: string,
): AdminOrderStatState & { resolveBrandName: BrandNameResolver; refetch: () => void } => {
  //* 상조명은 작은 이름 맵 하나로 붙인다 — 그래야 문서마다 브랜드를 populate하지 않는다.
  //* 맵이 오기 전에 큰 조회를 쏘면 populate 여부를 못 정하므로 한 박자 기다린다
  //* (맵은 세션 캐시라 두 번째부터는 즉시 ready).
  const { status: brandStatus, resolveBrandName, brandPopulateFallback } =
    useAnalyticsBrandNames();

  const enabled = !!from && !!to && from <= to && brandStatus !== 'loading';
  const key = `adminOrderStat|${kind}|${from}|${to}|${brandPopulateFallback ? 'pop' : '-'}`;

  const { data, loading, error, refetch } = useCachedResource<OrderStatPayload>(
    key,
    async () => {
      try {
        const { data: rows } = await api_.get<OrderStatOrder[]>(
          buildUrl(kind, from, to, brandPopulateFallback),
        );
        const orders = Array.isArray(rows) ? rows : [];
        return { orders, truncated: orders.length >= ORDER_FETCH_LIMIT };
      } catch (err) {
        throw toStatError(err);
      }
    },
    { enabled, errorMessage: '통계를 불러오지 못했습니다.' },
  );

  return {
    status: error ? 'error' : loading ? 'loading' : data ? 'success' : 'idle',
    orders: data?.orders ?? EMPTY_ORDERS,
    truncated: data?.truncated ?? false,
    errorMessage: error,
    resolveBrandName,
    refetch,
  };
};
